import {
  useAuthStore,
  useUserRole,
  useIsAdmin,
  useIsMineMinerRep,
  useIsMineMinerAdmin,
  useTokenExpiresAt,
  useSessionWarning,
} from '../../application/store/authStore';
import { CurrentUser } from '../../domain/entities/User';
import { UserRole } from '../../domain/value-objects/UserRole';
import { LoginCommand } from '../../application/dto/auth/LoginCommand';

/**
 * useAuth — hook for the MineMiner SPA's authentication surface.
 *
 * sprint-104 (epic-013 Sprint B, step 2d / S5). Slimmed from `Frontend/`'s
 * useAuth: only login / logout / loadCurrentUser are real actions. The
 * session-timeout warning surface is preserved so `SessionTimeoutWarning`
 * still renders; `extendSession` is a no-op stub (the carved host has no
 * refresh endpoint).
 */
export interface UseAuthResult {
  // Core state
  user: CurrentUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  role: UserRole | null;
  isAdmin: boolean;
  isMineMinerRep: boolean;
  isMineMinerAdmin: boolean;

  // Session state
  tokenExpiresAt: string | null;
  sessionWarningShown: boolean;

  // Actions
  login: (command: LoginCommand) => Promise<void>;
  logout: () => Promise<void>;
  loadCurrentUser: () => Promise<void>;
  clearError: () => void;

  showSessionWarning: () => void;
  hideSessionWarning: () => void;
  extendSession: () => Promise<void>;
}

export function useAuth(): UseAuthResult {
  const user = useAuthStore((state) => state.user);
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const isLoading = useAuthStore((state) => state.isLoading);
  const error = useAuthStore((state) => state.error);

  const tokenExpiresAt = useTokenExpiresAt();
  const sessionWarningShown = useSessionWarning();

  const login = useAuthStore((state) => state.login);
  const logout = useAuthStore((state) => state.logout);
  const loadCurrentUser = useAuthStore((state) => state.loadCurrentUser);
  const clearError = useAuthStore((state) => state.clearError);
  const showSessionWarning = useAuthStore((state) => state.showSessionWarning);
  const hideSessionWarning = useAuthStore((state) => state.hideSessionWarning);
  const extendSession = useAuthStore((state) => state.extendSession);

  const role = useUserRole();
  const isAdmin = useIsAdmin();
  const isMineMinerRep = useIsMineMinerRep();
  const isMineMinerAdmin = useIsMineMinerAdmin();

  return {
    user,
    isAuthenticated,
    isLoading,
    error,
    role,
    isAdmin,
    isMineMinerRep,
    isMineMinerAdmin,
    tokenExpiresAt,
    sessionWarningShown,
    login,
    logout,
    loadCurrentUser,
    clearError,
    showSessionWarning,
    hideSessionWarning,
    extendSession,
  };
}
